import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import EngineerLayout from "../components/EngineerLayout";
import { appendAuthToken } from "../utils/auth";
import VoucherSimpleManager from "./VoucherSimpleManager";

const emptyExpenseRow = () => ({
  expenseType: "",
  amount: "",
  kilometers: "",
  description: "",
  isSaved: false,
  isEditable: true,
  sourceId: ""
});

const toSavedExpenseRow = (row = {}) => ({
  expenseType: String(row.expenseType || "").trim(),
  amount: String(row.amount ?? ""),
  kilometers: Number(row.kilometers || 0) > 0 ? String(row.kilometers) : "",
  description: String(row.description || "").trim(),
  isSaved: true,
  isEditable: false,
  sourceId: String(row._id || row.id || "")
});

const rowHasContent = (row = {}) =>
  [row.expenseType, row.amount, row.kilometers, row.description].some((value) => String(value || "").trim() !== "");

const isPetrolExpense = (value = "") => String(value || "").trim().toLowerCase() === "petrol";

const SORT_OPTIONS = [
  { value: "voucherDate", label: "Date" },
  { value: "entryType", label: "Complaint / General" },
  { value: "expenseType", label: "Debit Type" },
  { value: "voucherId", label: "Voucher ID" },
  { value: "allowanceAmount", label: "Credit (Allowance)" },
  { value: "amount", label: "Debit (Expense)" },
  { value: "balance", label: "Balance" }
];

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const formatAddress = (address) => {
  if (!address) return "-";
  if (typeof address === "string") return address;

  return [address.street, address.area, address.city, address.state, address.pincode]
    .filter(Boolean)
    .join(", ");
};

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB");
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2
  });
};

const getExpenseMode = (voucher = {}) =>
  voucher.expenseMode === "complaint" || voucher.entryType === "complaint" ? "complaint" : "general";

const getEntryLabel = (entryType) =>
  getExpenseMode({ entryType, expenseMode: entryType }) === "complaint" ? "Complaint Expense" : "General Expense";

const sortValue = (voucher, field) => {
  if (field === "voucherDate") return new Date(voucher.date || voucher.voucherDate || voucher.createdAt || 0).getTime();
  if (field === "entryType") return String(voucher.complaintGeneral || getEntryLabel(voucher.expenseMode)).toLowerCase();
  if (field === "expenseType") return String(voucher.expenseSummary || voucher.details?.map((item) => item.title).join(", ") || "").toLowerCase();
  if (field === "voucherId") return String(voucher.expenseGroupId || "").toLowerCase();
  if (field === "allowanceAmount") return Number(voucher.allowanceAmount || 0);
  if (field === "amount") return Number(voucher.expenseAmount || 0);
  if (field === "balance") return Number(voucher.computedBalance || 0);
  return "";
};

function LegacyVouchers() {
  const engineerName = localStorage.getItem("engineerName") || "";
  const engineerEmail = localStorage.getItem("engineerEmail") || "";
  const engineerId = localStorage.getItem("engineerId") || "";
  const engineerLookup =
    engineerId ||
    engineerEmail ||
    engineerName;

  const [engineerProfile, setEngineerProfile] = useState(null);
  const [entryType, setEntryType] = useState("complaint");
  const [voucherDate, setVoucherDate] = useState(todayInputValue());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ field: "voucherDate", direction: "desc" });
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showAllowanceForm, setShowAllowanceForm] = useState(false);
  const [showClearDateModal, setShowClearDateModal] = useState(false);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [clearVoucherDate, setClearVoucherDate] = useState(todayInputValue());
  const [clearDateConfirmationText, setClearDateConfirmationText] = useState("");
  const [clearAllConfirmationText, setClearAllConfirmationText] = useState("");
  const [complaints, setComplaints] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statementData, setStatementData] = useState({
    rows: [],
    openingBalance: 0,
    currentBalance: 0,
    lastClearedAt: null,
    totals: { totalExpense: 0, totalAllowance: 0 }
  });
  const [balanceInfo, setBalanceInfo] = useState({ openingBalance: 0, currentBalance: 0, lastClearedAt: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingExistingVoucher, setLoadingExistingVoucher] = useState(false);
  const [openingVoucherGroupId, setOpeningVoucherGroupId] = useState("");
  const [savingAllowance, setSavingAllowance] = useState(false);
  const [clearingVoucher, setClearingVoucher] = useState(false);
  const [clearingAllVouchers, setClearingAllVouchers] = useState(false);
  const [vouchersLimit, setVouchersLimit] = useState(20);
  const [form, setForm] = useState({
    voucherTitle: "",
    complaintId: "",
    location: ""
  });
  const [expenseRows, setExpenseRows] = useState([emptyExpenseRow()]);
  const [voucherMode, setVoucherMode] = useState("create");
  const [activeVoucherGroupId, setActiveVoucherGroupId] = useState("");
  const [allowanceForm, setAllowanceForm] = useState({
    amount: "",
    givenByRole: "",
    givenByName: "",
    allowanceDate: todayInputValue(),
    notes: ""
  });

  const engineerMatches = useMemo(
    () =>
      [
        engineerName,
        engineerEmail,
        engineerId,
        engineerLookup,
        engineerProfile?._id,
        engineerProfile?.engineerId,
        engineerProfile?.email,
        engineerProfile?.name
      ]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean),
    [engineerEmail, engineerId, engineerLookup, engineerName, engineerProfile]
  );

  const canonicalEngineerId = engineerProfile?.engineerId || engineerId || engineerLookup;
  const canonicalEngineerName = engineerProfile?.name || engineerName || canonicalEngineerId;
  const engineerAliases = useMemo(
    () =>
      [
        canonicalEngineerId,
        canonicalEngineerName,
        engineerProfile?._id,
        engineerProfile?.email,
        engineerId,
        engineerEmail,
        engineerName,
        engineerLookup
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    [canonicalEngineerId, canonicalEngineerName, engineerEmail, engineerId, engineerLookup, engineerName, engineerProfile]
  );

  useEffect(() => {
    let alive = true;
    if (!engineerLookup) return undefined;

    axios.get(API_BASE_URL + `/api/engineers/profile/${encodeURIComponent(engineerLookup)}`)
      .then((response) => {
        if (alive) setEngineerProfile(response.data || null);
      })
      .catch(() => {
        if (alive) setEngineerProfile(null);
      });

    return () => {
      alive = false;
    };
  }, [engineerLookup]);

  const fetchPageData = useCallback(async () => {
    try {
      setLoading(true);
      const [complaintsResult, customersResult, statementResult] = await Promise.allSettled([
        axios.get(API_BASE_URL + "/api/complaints"),
        axios.get(API_BASE_URL + "/api/customers"),
        axios.get(API_BASE_URL + "/api/vouchers/statement", {
          params: { engineer: canonicalEngineerId || engineerAliases.join(","), engineerId: canonicalEngineerId, ownerRole: "engineer" }
        })
      ]);

      const complaintData =
        complaintsResult.status === "fulfilled" && Array.isArray(complaintsResult.value.data)
          ? complaintsResult.value.data
          : [];

      setComplaints(
        complaintData.filter((complaint) =>
          engineerMatches.includes(String(complaint.engineerAssigned || "").trim().toLowerCase())
        )
      );

      setCustomers(
        customersResult.status === "fulfilled" && Array.isArray(customersResult.value.data)
          ? customersResult.value.data
          : []
      );

      const nextStatement =
        statementResult.status === "fulfilled" && statementResult.value?.data
          ? statementResult.value.data
          : { rows: [], openingBalance: 0, currentBalance: 0, lastClearedAt: null, totals: { totalExpense: 0, totalAllowance: 0 } };

      setStatementData(nextStatement);
      setBalanceInfo({
        openingBalance: Number(nextStatement.openingBalance || 0),
        currentBalance: Number(nextStatement.currentBalance || 0),
        lastClearedAt: nextStatement.lastClearedAt || null
      });
    } catch (error) {
      console.error("Failed to load voucher page", error);
      setComplaints([]);
      setCustomers([]);
      setStatementData({ rows: [], openingBalance: 0, currentBalance: 0, lastClearedAt: null, totals: { totalExpense: 0, totalAllowance: 0 } });
      setBalanceInfo({ openingBalance: 0, currentBalance: 0, lastClearedAt: null });
    } finally {
      setLoading(false);
    }
  }, [canonicalEngineerId, engineerAliases, engineerMatches]);

  useEffect(() => {
    if (!engineerLookup) return;
    fetchPageData();
  }, [engineerLookup, fetchPageData]);

  const customerMap = useMemo(
    () => new Map(customers.map((customer) => [String(customer._id), customer])),
    [customers]
  );

  const complaintOptions = useMemo(
    () =>
      complaints.map((complaint) => {
        const customer = customerMap.get(String(complaint.customerId));
        return {
          ...complaint,
          destination: formatAddress(customer?.address)
        };
      }),
    [complaints, customerMap]
  );

  const selectedComplaint = complaintOptions.find((complaint) => complaint._id === form.complaintId);
  const pendingExpenseTotal = useMemo(
    () =>
      expenseRows.reduce(
        (sum, row) => sum + (!row.isSaved && row.isEditable !== false ? Number(row.amount || 0) : 0),
        0
      ),
    [expenseRows]
  );
  const visibleStatementRows = useMemo(
    () => (Array.isArray(statementData.rows) ? statementData.rows : []),
    [statementData.rows]
  );

  useEffect(() => {
    let alive = true;

    if (!showExpenseForm) {
      return undefined;
    }

    const trimmedTitle = String(form.voucherTitle || "").trim();
    const shouldLookupComplaint = entryType !== "complaint" || Boolean(form.complaintId);

    if (!trimmedTitle || !voucherDate || !canonicalEngineerId || !shouldLookupComplaint) {
      setLoadingExistingVoucher(false);
      setVoucherMode("create");
      setActiveVoucherGroupId("");
      setExpenseRows((current) => {
        const editableRows = current.filter((row) => !row.isSaved);
        return editableRows.length > 0 ? editableRows : [emptyExpenseRow()];
      });
      return undefined;
    }

    const loadExistingVoucher = async () => {
      try {
        setLoadingExistingVoucher(true);
        const response = await axios.get(API_BASE_URL + "/api/vouchers", {
          params: {
            ownerRole: "engineer",
            engineer: canonicalEngineerId || engineerAliases.join(","),
            engineerId: canonicalEngineerId,
            voucherDate,
            voucherTitle: trimmedTitle,
            entryType,
            ...(entryType === "complaint" && form.complaintId ? { complaintId: form.complaintId } : {})
          }
        });

        if (!alive) return;

        const matchingVouchers = Array.isArray(response.data) ? response.data : [];
        if (matchingVouchers.length === 0) {
          setVoucherMode("create");
          setActiveVoucherGroupId("");
          setExpenseRows((current) => {
            const editableRows = current.filter((row) => !row.isSaved);
            return editableRows.length > 0 ? editableRows : [emptyExpenseRow()];
          });
          return;
        }

        const sortedVouchers = [...matchingVouchers].sort(
          (first, second) => new Date(first.createdAt || first.voucherDate || 0) - new Date(second.createdAt || second.voucherDate || 0)
        );
        const firstVoucher = sortedVouchers[0];

        setVoucherMode("edit");
        setActiveVoucherGroupId(firstVoucher.expenseGroupId || "");
        setForm((current) => ({
          ...current,
          voucherTitle: firstVoucher.voucherTitle || trimmedTitle,
          complaintId: firstVoucher.complaintId || current.complaintId,
          location: firstVoucher.location || current.location
        }));
        setExpenseRows([...sortedVouchers.map(toSavedExpenseRow), emptyExpenseRow()]);
      } catch (error) {
        if (alive) {
          setVoucherMode("create");
          setActiveVoucherGroupId("");
        }
      } finally {
        if (alive) setLoadingExistingVoucher(false);
      }
    };

    loadExistingVoucher();

    return () => {
      alive = false;
    };
  }, [
    canonicalEngineerId,
    engineerAliases,
    entryType,
    form.complaintId,
    form.voucherTitle,
    showExpenseForm,
    voucherDate
  ]);

  const filteredVouchers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    const dateFiltered = visibleStatementRows.filter((voucher) => {
      const dateValue = String(voucher.date || "").slice(0, 10);
      if (fromDate && dateValue < fromDate) return false;
      if (toDate && dateValue > toDate) return false;
      return true;
    });

    const searched = query
        ? dateFiltered.filter((voucher) => {
          const expenseLines = voucher.details
            .map((row) => [row.title, row.meta].filter(Boolean).join(" - "))
            .join(" ");
          const searchable = [
            formatDisplayDate(voucher.date),
            voucher.expenseGroupId,
            voucher.complaintGeneral,
            voucher.refText,
            expenseLines,
            voucher.allowanceAmount,
            voucher.expenseAmount,
            voucher.computedBalance,
          ].join(" ").toLowerCase();

          return searchable.includes(query);
        })
      : dateFiltered;

    return [...searched].sort((first, second) => {
      const firstValue = sortValue(first, sortConfig.field);
      const secondValue = sortValue(second, sortConfig.field);
      const direction = sortConfig.direction === "asc" ? 1 : -1;

      if (firstValue > secondValue) return direction;
      if (firstValue < secondValue) return -direction;

      const firstTime = new Date(first.createdAt || first.date || 0).getTime();
      const secondTime = new Date(second.createdAt || second.date || 0).getTime();
      if (firstTime > secondTime) return direction;
      if (firstTime < secondTime) return -direction;
      return String(first.id || first._id || "").localeCompare(String(second.id || second._id || ""));
    });
  }, [fromDate, searchTerm, sortConfig, toDate, visibleStatementRows]);

  const displayedVouchers = useMemo(() => {
    return filteredVouchers.slice(0, vouchersLimit);
  }, [filteredVouchers, vouchersLimit]);

  const activeBalance = Number(statementData.currentBalance || 0);

  const totals = useMemo(() => {
    const amount = filteredVouchers.reduce((sum, voucher) => sum + Number(voucher.expenseAmount || 0), 0);
    const allowanceAmount = filteredVouchers.reduce((sum, voucher) => sum + Number(voucher.allowanceAmount || 0), 0);

    return {
      allowanceAmount,
      amount,
      balance: Number(statementData.currentBalance || 0)
    };
  }, [filteredVouchers, statementData.currentBalance]);

  const clearVoucherPreview = useMemo(() => {
    const voucherGroups = visibleStatementRows.filter(
      (voucher) => voucher.kind !== "opening" && String(voucher.date || "").slice(0, 10) === clearVoucherDate
    );

    return {
      voucherCount: voucherGroups.length,
      totalAmount: voucherGroups.reduce(
        (sum, voucher) => sum + Number(voucher.expenseAmount || 0) + Number(voucher.allowanceAmount || 0),
        0
      )
    };
  }, [clearVoucherDate, visibleStatementRows]);

  const clearAllPreview = useMemo(() => ({
    voucherCount: visibleStatementRows.filter((row) => row.kind !== "opening").length,
    expenseTotal: Number(statementData.totals?.totalExpense || 0),
    allowanceTotal: Number(statementData.totals?.totalAllowance || 0),
    preservedBalance: Number(statementData.currentBalance || 0)
  }), [statementData.currentBalance, statementData.totals, visibleStatementRows]);

  const recentAllowanceRows = useMemo(
    () => visibleStatementRows.filter((row) => row.kind === "allowance").slice(0, 4),
    [visibleStatementRows]
  );

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setForm({
      voucherTitle: "",
      complaintId: "",
      location: ""
    });
    setVoucherMode("create");
    setActiveVoucherGroupId("");
    setExpenseRows([emptyExpenseRow()]);
  };

  const startNewVoucher = () => {
    resetForm();
    setVoucherDate(todayInputValue());
    setEntryType("complaint");
  };

  const updateExpenseRow = (index, field, value) => {
    setExpenseRows((current) =>
      current.map((row, rowIndex) => (
        rowIndex === index && row.isEditable !== false
          ? { ...row, [field]: value }
          : row
      ))
    );
  };

  const addExpenseRow = () => {
    setExpenseRows((current) => [...current, emptyExpenseRow()]);
  };

  const addPetrolExpenseRow = () => {
    setExpenseRows((current) => [
      ...current,
      { ...emptyExpenseRow(), expenseType: "petrol" }
    ]);
  };

  const removeExpenseRow = (index) => {
    setExpenseRows((current) => {
      const targetRow = current[index];
      if (!targetRow || targetRow.isSaved || targetRow.isEditable === false) {
        return current;
      }

      const nextRows = current.filter((_, rowIndex) => rowIndex !== index);
      const editableRows = nextRows.filter((row) => !row.isSaved);

      if (editableRows.length === 0) {
        return [...nextRows, emptyExpenseRow()];
      }

      return nextRows;
    });
  };

  const updateAllowanceForm = (field, value) => {
    setAllowanceForm((current) => ({ ...current, [field]: value }));
  };

  const submitAllowance = async () => {
    const amount = Number(allowanceForm.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Please enter a valid allowance amount");
      return;
    }

    if (!allowanceForm.givenByRole) {
      alert("Please choose whether Admin or Accounts gave the allowance");
      return;
    }

    if (!allowanceForm.givenByName.trim()) {
      alert("Please enter the name of the person who gave the allowance");
      return;
    }

    try {
      setSavingAllowance(true);

      const payload = {
        ownerRole: "engineer",
        engineerId: canonicalEngineerId,
        engineerName: canonicalEngineerName,
        createdByUserId: canonicalEngineerId,
        entryType: "non-complaint",
        location: "Common allowance",
        allowanceDate: allowanceForm.allowanceDate,
        amount,
        givenByRole: allowanceForm.givenByRole,
        givenByName: allowanceForm.givenByName,
        notes: allowanceForm.notes
      };

      await axios.post(API_BASE_URL + "/api/vouchers/allowances", payload);
      setAllowanceForm({
        amount: "",
        givenByRole: "",
        givenByName: "",
        allowanceDate: todayInputValue(),
        notes: ""
      });
      await fetchPageData();
      setShowAllowanceForm(false);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save allowance");
    } finally {
      setSavingAllowance(false);
    }
  };

  const submitVoucher = async () => {
    const trimmedVoucherTitle = String(form.voucherTitle || "").trim();

    if (!trimmedVoucherTitle) {
      alert("Please enter voucher title");
      return;
    }

    if (entryType === "complaint" && !selectedComplaint) {
      alert("Please choose a complaint");
      return;
    }

    const rows = expenseRows
      .filter((row) => !row.isSaved && row.isEditable !== false)
      .filter((row) => rowHasContent(row))
      .map((row, index) => ({
        ...row,
        expenseType: String(row.expenseType || "").trim(),
        amount: Number(row.amount),
        kilometers: Number(row.kilometers || 0),
        rowNumber: index + 1
      }));

    if (rows.length === 0) {
      alert(voucherMode === "edit" ? "Please add at least one new expense row" : "Please add at least one expense row");
      return;
    }

    const invalidRow = rows.find(
      (row) =>
        !row.expenseType ||
        !Number.isFinite(row.amount) ||
        row.amount <= 0 ||
        (isPetrolExpense(row.expenseType) && (!Number.isFinite(row.kilometers) || row.kilometers <= 0))
    );

    if (invalidRow) {
      alert(`Please complete expense type, amount${isPetrolExpense(invalidRow.expenseType) ? ", and KMs" : ""} in row ${invalidRow.rowNumber}`);
      return;
    }

    try {
      setSaving(true);
      const basePayload = {
        ownerRole: "engineer",
        engineerId: canonicalEngineerId,
        engineerName: canonicalEngineerName,
        createdByUserId: canonicalEngineerId,
        voucherDate,
        voucherTitle: trimmedVoucherTitle,
        entryType,
        expenseMode: entryType === "complaint" ? "complaint" : "general",
        ...(activeVoucherGroupId ? { expenseGroupId: activeVoucherGroupId } : {}),
        entries: rows.map((row) => ({
          expenseType: row.expenseType,
          amount: row.amount,
          kilometers: isPetrolExpense(row.expenseType) ? row.kilometers : 0,
          description: row.description
        }))
      };

      const payload =
        entryType === "complaint"
          ? {
              ...basePayload,
              complaintId: selectedComplaint._id,
              complaintRefId: selectedComplaint.complaintId,
              customerId: selectedComplaint.customerId,
              company: selectedComplaint.company,
              location: selectedComplaint.destination
            }
          : {
              ...basePayload,
              location: "General expense"
            };

      const response = await axios.post(API_BASE_URL + "/api/vouchers", payload);
      const savedRows = Array.isArray(response.data?.vouchers)
        ? response.data.vouchers.map(toSavedExpenseRow)
        : rows.map((row) => toSavedExpenseRow(row));

      setVoucherMode("edit");
      setActiveVoucherGroupId(response.data?.expenseGroupId || activeVoucherGroupId);
      setExpenseRows((current) => [
        ...current.filter((row) => row.isSaved),
        ...savedRows,
        emptyExpenseRow()
      ]);
      if (fromDate && voucherDate < fromDate) setFromDate(voucherDate);
      if (toDate && voucherDate > toDate) setToDate(voucherDate);
      await fetchPageData();
    } catch (error) {
      if (error.response?.status === 404) {
        alert("Voucher backend route is not available yet. Please restart the backend server that is running on port 5000.");
      } else {
        alert(error.response?.data?.message || "Failed to save voucher");
      }
    } finally {
      setSaving(false);
    }
  };

  const openVoucherForEdit = async (voucher) => {
    if (!voucher || voucher.kind !== "expense" || !voucher.expenseGroupId) {
      return;
    }

    try {
      setOpeningVoucherGroupId(voucher.expenseGroupId);
      setShowAllowanceForm(false);

      const response = await axios.get(API_BASE_URL + "/api/vouchers", {
        params: {
          ownerRole: "engineer",
          engineer: canonicalEngineerId || engineerAliases.join(","),
          engineerId: canonicalEngineerId,
          expenseGroupId: voucher.expenseGroupId
        }
      });

      const rows = Array.isArray(response.data) ? response.data : [];
      if (rows.length === 0) {
        alert("Unable to open this voucher");
        return;
      }

      const sortedRows = [...rows].sort(
        (first, second) => new Date(first.createdAt || first.voucherDate || 0) - new Date(second.createdAt || second.voucherDate || 0)
      );
      const firstRow = sortedRows[0];

      setEntryType(firstRow.entryType === "complaint" ? "complaint" : "non-complaint");
      setVoucherDate(String(firstRow.voucherDate || "").slice(0, 10) || todayInputValue());
      setForm({
        voucherTitle: firstRow.voucherTitle || "",
        complaintId: firstRow.complaintId || "",
        location: firstRow.location || ""
      });
      setVoucherMode("edit");
      setActiveVoucherGroupId(firstRow.expenseGroupId || voucher.expenseGroupId);
      setExpenseRows([...sortedRows.map(toSavedExpenseRow), emptyExpenseRow()]);
      setShowExpenseForm(true);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to open voucher");
    } finally {
      setOpeningVoucherGroupId("");
    }
  };

  const clearVouchersForDate = async () => {
    if (clearDateConfirmationText !== "YES") return;

    try {
      setClearingVoucher(true);
      const response = await axios.post(API_BASE_URL + "/api/vouchers/clear-by-date", {
        voucherDate: clearVoucherDate,
        ownerRole: "engineer",
        engineerId: canonicalEngineerId,
        engineerName: canonicalEngineerName,
        createdByUserId: canonicalEngineerId,
        requestedByRole: "engineer"
      });

      alert(response.data?.message || "Voucher cleared successfully");
      setShowClearDateModal(false);
      setClearDateConfirmationText("");
      await fetchPageData();
    } catch (error) {
      if (error.response?.status === 404) {
        alert("Clear voucher route is not available yet. Please restart the backend server.");
      } else {
        alert(error.response?.data?.message || "Failed to clear vouchers");
      }
    } finally {
      setClearingVoucher(false);
    }
  };

  const clearAllVouchers = async () => {
    if (clearAllConfirmationText !== "YES") return;

    try {
      setClearingAllVouchers(true);
      const response = await axios.post(API_BASE_URL + "/api/vouchers/clear-all", {
        ownerRole: "engineer",
        engineerId: canonicalEngineerId,
        engineerName: canonicalEngineerName,
        createdByUserId: canonicalEngineerId,
        requestedByRole: "engineer"
      });

      alert(response.data?.message || "All vouchers cleared successfully");
      setShowClearAllModal(false);
      setClearAllConfirmationText("");
      await fetchPageData();
    } catch (error) {
      if (error.response?.status === 404) {
        alert("Clear all voucher route is not available yet. Please restart the backend server.");
      } else {
        alert(error.response?.data?.message || "Failed to clear all vouchers");
      }
    } finally {
      setClearingAllVouchers(false);
    }
  };

  const downloadVoucherPdf = () => {
    const params = new URLSearchParams({
      ownerRole: "engineer",
      engineer: canonicalEngineerId || engineerAliases.join(","),
      engineerId: canonicalEngineerId || ""
    });

    if (fromDate) params.set("fromDate", fromDate);
    if (toDate) params.set("toDate", toDate);
    params.set("download", "1");

    window.open(appendAuthToken(API_BASE_URL + `/api/vouchers/statement/pdf?${params.toString()}`), "_blank", "noopener,noreferrer");
  };

  const downloadSingleVoucherPdf = (voucher) => {
    const targetId = voucher.expenseGroupId || voucher._id;
    if (!targetId) return;
    const url = appendAuthToken(API_BASE_URL + `/api/vouchers/${targetId}/pdf?download=1`);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <EngineerLayout>
      <div className="mobile-dense-page mx-auto max-w-7xl space-y-4 lg:space-y-6">
        <section className="rounded-[30px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_34%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(224,231,255,0.72))] p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-700">Vouchers</p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Expense Log</h1>
            </div>

            <div className="rounded-2xl border border-emerald-100 bg-white/80 px-4 py-3 text-sm text-slate-700">
              Engineer: <span className="font-semibold text-slate-900">{canonicalEngineerName || engineerLookup || "-"}</span>
            </div>
          </div>
        </section>

        {showAllowanceForm && (
        <section className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Add Credit (Allowance)</h2>
            </div>
            <button
              type="button"
              onClick={() => setShowAllowanceForm(false)}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          <div className="rounded-[24px] border border-emerald-100 bg-emerald-50/60 p-5">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Credit Details</h3>
              </div>
              <div className="rounded-2xl bg-white px-4 py-3 text-sm ring-1 ring-emerald-100">
                <span className="font-semibold text-slate-600">Current Balance: </span>
                <span className={`font-bold ${activeBalance < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                  {formatCurrency(activeBalance)}
                </span>
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Credit Amount (Allowance)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={allowanceForm.amount}
                  onChange={(event) => updateAllowanceForm("amount", event.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Given By</label>
                <select
                  value={allowanceForm.givenByRole}
                  onChange={(event) => updateAllowanceForm("givenByRole", event.target.value)}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                >
                  <option value="">Select</option>
                  <option value="admin">Admin</option>
                  <option value="accounts">Accounts</option>
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Name</label>
                <input
                  value={allowanceForm.givenByName}
                  onChange={(event) => updateAllowanceForm("givenByName", event.target.value)}
                  placeholder={allowanceForm.givenByRole ? `${allowanceForm.givenByRole} name` : "Person name"}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Given Date</label>
                <input
                  type="date"
                  value={allowanceForm.allowanceDate}
                  onChange={(event) => updateAllowanceForm("allowanceDate", event.target.value)}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={submitAllowance}
                  disabled={savingAllowance}
                  className="w-full rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {savingAllowance ? "Saving..." : "Save Credit"}
                </button>
              </div>

              <div className="md:col-span-2 xl:col-span-5">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Credit Notes</label>
                <input
                  value={allowanceForm.notes}
                  onChange={(event) => updateAllowanceForm("notes", event.target.value)}
                  placeholder="Optional note for this credit"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                />
              </div>
            </div>

            {recentAllowanceRows.length > 0 && (
              <div className="mt-5 rounded-2xl bg-white p-4 ring-1 ring-emerald-100">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">Credit History</p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  {recentAllowanceRows.map((allowance) => (
                    <div key={allowance.id || allowance._id} className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
                      <p className="font-semibold text-slate-900">{formatCurrency(allowance.allowanceAmount)}</p>
                      <p className="mt-1">
                        {allowance.details?.[0]?.title || "Credit"} - {formatDisplayDate(allowance.date)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
        )}

        {showExpenseForm && (
        <section className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">{voucherMode === "edit" ? "Add Debit to Existing Voucher" : "Create Voucher"}</h2>
              <p className="mt-1 text-sm text-slate-500">
                {voucherMode === "edit"
                  ? "Saved rows are locked. Only new rows can be added and submitted."
                  : "Create a new voucher with title, date, and one or more debit rows."}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {voucherMode === "edit" ? (
                <button
                  type="button"
                  onClick={startNewVoucher}
                  className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                >
                  Create New Voucher
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setShowExpenseForm(false)}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>

          {voucherMode === "edit" ? (
            <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
              Add Debit to Existing Voucher
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[auto_220px] lg:items-end">
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setEntryType("complaint")}
                disabled={voucherMode === "edit"}
                className={`rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
                  entryType === "complaint" ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                } ${voucherMode === "edit" ? "cursor-not-allowed opacity-60" : ""}`}
              >
                Complaint Debit (Expense)
              </button>
              <button
                type="button"
                onClick={() => setEntryType("non-complaint")}
                disabled={voucherMode === "edit"}
                className={`rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
                  entryType === "non-complaint" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                } ${voucherMode === "edit" ? "cursor-not-allowed opacity-60" : ""}`}
              >
                General Debit (Expense)
              </button>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Voucher Date</label>
              <input
                type="date"
                value={voucherDate}
                onChange={(event) => setVoucherDate(event.target.value)}
                disabled={voucherMode === "edit"}
                className={`w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 ${voucherMode === "edit" ? "cursor-not-allowed bg-slate-100 text-slate-500" : ""}`}
              />
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-semibold text-slate-700">Voucher Title</label>
              <input
                value={form.voucherTitle}
                onChange={(event) => updateForm("voucherTitle", event.target.value)}
                placeholder="Enter voucher title"
                disabled={voucherMode === "edit"}
                className={`w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 ${voucherMode === "edit" ? "cursor-not-allowed bg-slate-100 text-slate-500" : ""}`}
              />
            </div>
            {entryType === "complaint" ? (
              <>
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Complaint</label>
                  <select
                    value={form.complaintId}
                    onChange={(event) => updateForm("complaintId", event.target.value)}
                    disabled={voucherMode === "edit"}
                    className={`w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 ${voucherMode === "edit" ? "cursor-not-allowed bg-slate-100 text-slate-500" : ""}`}
                  >
                    <option value="">Select complaint</option>
                    {complaintOptions.map((complaint) => (
                      <option key={complaint._id} value={complaint._id}>
                        {complaint.complaintId} - {complaint.company} - {complaint.destination}
                      </option>
                    ))}
                  </select>
                </div>

                <ReadOnlyField label="Customer" value={selectedComplaint?.company || "-"} />
                <ReadOnlyField label="Location" value={selectedComplaint?.destination || "-"} />
              </>
            ) : null}
          </div>

          <div className="mt-6">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <ReadOnlyField
                label="Balance After These Debits"
                value={formatCurrency(activeBalance - pendingExpenseTotal)}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={addPetrolExpenseRow}
                  className="rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
                >
                  + Petrol Debit
                </button>
                <button
                  type="button"
                  onClick={addExpenseRow}
                  className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                >
                  + Add Debit Row
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="bg-slate-50 text-left text-slate-700">
                    <tr>
                      <th className="px-3 py-3 font-bold">S.No</th>
                      <th className="px-3 py-3 font-bold">Status</th>
                      <th className="px-3 py-3 font-bold">Debit Type</th>
                      <th className="px-3 py-3 font-bold">Debit Amount</th>
                      <th className="px-3 py-3 font-bold">KMs</th>
                      <th className="px-3 py-3 font-bold">Remarks</th>
                      <th className="px-3 py-3 font-bold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {expenseRows.map((row, index) => {
                      const rowLocked = row.isEditable === false || row.isSaved;
                      const inputClassName = `w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 ${rowLocked ? "cursor-not-allowed bg-slate-100 text-slate-500" : ""}`;

                      return (
                      <tr key={row.sourceId || index} className={`align-top ${rowLocked ? "bg-slate-50/70" : ""}`}>
                        <td className="px-3 py-3 font-semibold text-slate-700">{index + 1}</td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${row.isSaved ? "bg-slate-200 text-slate-700" : "bg-emerald-100 text-emerald-700"}`}>
                            {row.isSaved ? "Saved" : "New"}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <input
                            value={row.expenseType}
                            onChange={(event) => updateExpenseRow(index, "expenseType", event.target.value)}
                            placeholder="Type debit"
                            disabled={rowLocked}
                            className={inputClassName}
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.amount}
                            onChange={(event) => updateExpenseRow(index, "amount", event.target.value)}
                            placeholder="0.00"
                            disabled={rowLocked}
                            className={inputClassName}
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.kilometers}
                            onChange={(event) => updateExpenseRow(index, "kilometers", event.target.value)}
                            placeholder={isPetrolExpense(row.expenseType) ? "Required" : "Only petrol"}
                            disabled={rowLocked}
                            className={inputClassName}
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            value={row.description}
                            onChange={(event) => updateExpenseRow(index, "description", event.target.value)}
                            placeholder="Remarks"
                            disabled={rowLocked}
                            className={inputClassName}
                          />
                        </td>
                        <td className="px-3 py-3">
                          {rowLocked ? (
                            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Locked</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => removeExpenseRow(index)}
                              className="rounded-xl bg-red-100 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-200"
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {loadingExistingVoucher ? (
            <p className="mt-4 text-sm font-medium text-slate-500">Checking for existing voucher rows...</p>
          ) : null}

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={submitVoucher}
              disabled={saving || loadingExistingVoucher}
              className="rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {saving ? "Saving..." : voucherMode === "edit" ? "Add Debit" : "Save Voucher"}
            </button>
          </div>
        </section>
        )}

        <section className="w-full max-w-full overflow-x-hidden rounded-[30px] border border-slate-200 bg-white p-4 shadow-sm md:p-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900 md:text-xl">Voucher Table</h2>
            </div>
            <div className="flex flex-col gap-3 lg:items-end">
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowAllowanceForm((current) => !current);
                    setShowExpenseForm(false);
                  }}
                  className="rounded-xl border border-emerald-200 bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                >
                  {showAllowanceForm ? "Hide Credit Form" : "+ Add Credit"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (showExpenseForm) {
                      setShowExpenseForm(false);
                    } else {
                      startNewVoucher();
                      setShowExpenseForm(true);
                    }
                    setShowAllowanceForm(false);
                  }}
                  className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  {showExpenseForm ? "Hide Debit Form" : "+ Add Debit"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setClearAllConfirmationText("");
                    setShowClearAllModal(true);
                  }}
                  className="rounded-xl border border-rose-300 bg-white px-5 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-50"
                >
                  Clear All Vouchers
                </button>
                <button
                  type="button"
                  onClick={downloadVoucherPdf}
                  className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Download PDF
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <SummaryPill label="Opening Balance" value={formatCurrency(balanceInfo.openingBalance)} />
                <SummaryPill label="Total Credit" value={formatCurrency(totals.allowanceAmount)} />
                <SummaryPill label="Current Balance" value={formatCurrency(balanceInfo.currentBalance)} />
              </div>
            </div>
          </div>

          <div className="mt-4 md:mt-6">
            <div className="md:hidden">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">Search</label>
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search voucher details..."
                className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
              />
            </div>

            <div className="hidden gap-4 lg:grid-cols-[1fr_180px_180px_220px_160px] md:grid">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Search</label>
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search type, complaint, expense, remarks, amount..."
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Sort By</label>
              <select
                value={sortConfig.field}
                onChange={(event) => setSortConfig((current) => ({ ...current, field: event.target.value }))}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Order</label>
              <select
                value={sortConfig.direction}
                onChange={(event) => setSortConfig((current) => ({ ...current, direction: event.target.value }))}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
              >
                <option value="desc">High to Low / Newest</option>
                <option value="asc">Low to High / Oldest</option>
              </select>
            </div>
            </div>
          </div>

          <div className="mt-4 space-y-3 md:hidden">
            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                Loading vouchers...
              </div>
            ) : filteredVouchers.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                No vouchers found for this search. Opening balance remains {formatCurrency(balanceInfo.currentBalance)}.
              </div>
            ) : (
              displayedVouchers.map((voucher) => {
                const allowanceAmount = Number(voucher.allowanceAmount || 0);
                const amount = Number(voucher.expenseAmount || 0);
                const balance = Number(voucher.computedBalance || 0);

                return (
                  <article key={`mobile-${voucher._id}`} className="w-full rounded-[14px] border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{formatDisplayDate(voucher.date)}</p>
                        <p className="mt-1 text-[11px] leading-4 text-slate-500">
                          {voucher.kind === "expense" ? `Voucher ID: ${voucher.expenseGroupId || "-"}` : "Credit Entry"}
                        </p>
                        {voucher.voucherTitle ? (
                          <p className="mt-1 text-[11px] font-medium leading-4 text-slate-600">Title: {voucher.voucherTitle}</p>
                        ) : null}
                        <p className="mt-1 text-[11px] leading-4 text-slate-500">{voucher.refText || "-"}</p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${voucher.badgeTone === "sky" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {voucher.complaintGeneral}
                      </span>
                    </div>

                    <div className="mt-3 space-y-2">
                      {voucher.details.map((row, index) => (
                        <div key={index} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900">{row.title || "-"}</div>
                              {row.meta ? <div className="mt-0.5 text-[11px] leading-4 text-slate-500">{row.meta}</div> : null}
                            </div>
                            <div className={`shrink-0 text-[11px] font-semibold ${voucher.kind === "expense" ? "text-rose-700" : "text-emerald-700"}`}>
                              {formatCurrency(row.amount)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Debit</p>
                        <p className="mt-1 text-xs font-semibold text-rose-700">{voucher.kind === "expense" ? formatCurrency(amount) : "-"}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Credit</p>
                        <p className="mt-1 text-xs font-semibold text-emerald-700">{voucher.kind === "allowance" ? formatCurrency(allowanceAmount) : "-"}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Balance</p>
                        <p className={`mt-1 text-xs font-bold ${balance < 0 ? "text-rose-700" : "text-emerald-700"}`}>{formatCurrency(balance)}</p>
                      </div>
                    </div>

                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => downloadSingleVoucherPdf(voucher)}
                        className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-100"
                        title="Download Voucher Slip PDF"
                      >
                        PDF
                      </button>
                      {voucher.kind === "expense" ? (
                        <button
                          type="button"
                          onClick={() => openVoucherForEdit(voucher)}
                          disabled={openingVoucherGroupId === voucher.expenseGroupId}
                          className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-[11px] font-semibold text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {openingVoucherGroupId === voucher.expenseGroupId ? "Opening..." : "Edit"}
                        </button>
                      ) : null}
                    </div>
                  </article>
                );
              })
            )}
          </div>

          <div className="mt-6 hidden overflow-hidden rounded-2xl border border-slate-200 md:block">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1050px] text-sm">
                <thead className="bg-slate-50 text-left text-slate-700">
                  <tr>
                    <th className="px-4 py-3 font-bold">Date</th>
                    <th className="px-4 py-3 font-bold">Complaint / General</th>
                    <th className="px-4 py-3 font-bold">Debit Details</th>
                    <th className="px-4 py-3 text-right font-bold">Debit</th>
                    <th className="px-4 py-3 text-right font-bold">Credit</th>
                    <th className="px-4 py-3 text-right font-bold">Balance</th>
                    <th className="px-4 py-3 text-center font-bold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-slate-500">Loading vouchers...</td>
                    </tr>
                  ) : filteredVouchers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                        No vouchers found for this filter. Opening balance remains {formatCurrency(balanceInfo.currentBalance)}.
                      </td>
                    </tr>
                  ) : (
                    displayedVouchers.map((voucher) => {
                      const allowanceAmount = Number(voucher.allowanceAmount || 0);
                      const amount = Number(voucher.expenseAmount || 0);
                      const balance = Number(voucher.computedBalance || 0);

                      return (
                        <tr key={voucher._id} className="align-top transition hover:bg-slate-50">
                          <td className="px-4 py-4 font-medium text-slate-800">
                            {formatDisplayDate(voucher.date)}
                            <div className="mt-2 text-xs text-slate-500">
                              {voucher.kind === "expense" ? `Voucher ID: ${voucher.expenseGroupId || "-"}` : "Credit Entry"}
                            </div>
                            {voucher.voucherTitle ? (
                              <div className="mt-1 text-xs text-slate-500">Title: {voucher.voucherTitle}</div>
                            ) : null}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${voucher.badgeTone === "sky" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                              {voucher.complaintGeneral}
                            </span>
                            <div className="mt-2 text-xs text-slate-500">
                              {voucher.refText || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            <div className="space-y-2 rounded-2xl bg-slate-50 p-3">
                              {voucher.details.map((row, index) => (
                                <div key={index} className="border-b border-slate-200 pb-2 last:border-b-0 last:pb-0">
                                  <div className="font-semibold text-slate-900">{row.title || "-"}</div>
                                  {row.meta ? (
                                    <div className="mt-1 text-xs text-slate-500">
                                      {row.meta}
                                    </div>
                                  ) : null}
                                  <div className={`mt-1 text-xs font-semibold ${voucher.kind === "expense" ? "text-rose-700" : "text-emerald-700"}`}>{formatCurrency(row.amount)}</div>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right font-semibold text-rose-700">
                            {voucher.kind === "expense" ? formatCurrency(amount) : ""}
                          </td>
                          <td className="px-4 py-4 text-right font-semibold text-emerald-700">
                            {voucher.kind === "allowance" ? formatCurrency(allowanceAmount) : ""}
                          </td>
                          <td className={`px-4 py-4 text-right font-bold ${balance < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                            {formatCurrency(balance)}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => downloadSingleVoucherPdf(voucher)}
                                className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
                                title="Download Voucher Slip PDF"
                              >
                                PDF
                              </button>
                              {voucher.kind === "expense" ? (
                                <button
                                  type="button"
                                  onClick={() => openVoucherForEdit(voucher)}
                                  disabled={openingVoucherGroupId === voucher.expenseGroupId}
                                  className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  {openingVoucherGroupId === voucher.expenseGroupId ? "Opening..." : "Edit"}
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination / View More for Vouchers */}
            {filteredVouchers.length > vouchersLimit && (
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="text-xs font-semibold text-slate-500">
                  Showing <span className="font-bold text-slate-800">{displayedVouchers.length}</span> of <span className="font-bold text-slate-800">{filteredVouchers.length}</span> entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setVouchersLimit((prev) => prev + 20)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-xs transition"
                  >
                    <span className="material-symbols-outlined text-sm font-bold">expand_more</span>
                    View More (+20)
                  </button>
                  <button
                    type="button"
                    onClick={() => setVouchersLimit(filteredVouchers.length)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold transition"
                  >
                    View All ({filteredVouchers.length})
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      {showClearDateModal && (
        <ConfirmationModal
          title="Clear Selected Date"
          clearVoucherDate={clearVoucherDate}
          setClearVoucherDate={setClearVoucherDate}
          clearVoucherPreview={clearVoucherPreview}
          clearConfirmationText={clearDateConfirmationText}
          setClearConfirmationText={setClearDateConfirmationText}
          clearingVoucher={clearingVoucher}
          onClose={() => {
            if (clearingVoucher) return;
            setShowClearDateModal(false);
            setClearDateConfirmationText("");
          }}
          onConfirm={clearVouchersForDate}
          confirmLabel="Clear"
        />
      )}
      {showClearAllModal && (
        <ClearAllConfirmationModal
          clearAllPreview={clearAllPreview}
          clearConfirmationText={clearAllConfirmationText}
          setClearConfirmationText={setClearAllConfirmationText}
          clearingVoucher={clearingAllVouchers}
          onClose={() => {
            if (clearingAllVouchers) return;
            setShowClearAllModal(false);
            setClearAllConfirmationText("");
          }}
          onConfirm={clearAllVouchers}
        />
      )}
    </EngineerLayout>
  );
}

function ConfirmationModal({
  title,
  clearVoucherDate,
  setClearVoucherDate,
  clearVoucherPreview,
  clearConfirmationText,
  setClearConfirmationText,
  clearingVoucher,
  onClose,
  onConfirm,
  confirmLabel
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4">
      <div className="w-full max-w-xl rounded-[28px] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-slate-900">{title}</h3>
            <p className="mt-2 text-sm text-slate-500">Only vouchers for the selected date will be cleared.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
          >
            Close
          </button>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-slate-700">Selected Date</span>
            <input
              type="date"
              value={clearVoucherDate}
              onChange={(event) => setClearVoucherDate(event.target.value)}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
            />
          </label>
          <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm text-slate-700">
            <p><span className="font-semibold text-slate-900">Voucher Count:</span> {clearVoucherPreview.voucherCount}</p>
            <p className="mt-2"><span className="font-semibold text-slate-900">Total Amount:</span> {formatCurrency(clearVoucherPreview.totalAmount)}</p>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-slate-700">Type YES to confirm</span>
            <input
              value={clearConfirmationText}
              onChange={(event) => setClearConfirmationText(event.target.value)}
              placeholder="YES"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={clearConfirmationText !== "YES" || clearVoucherPreview.voucherCount === 0 || clearingVoucher}
            className="rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {clearingVoucher ? "Clearing..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClearAllConfirmationModal({
  clearAllPreview,
  clearConfirmationText,
  setClearConfirmationText,
  clearingVoucher,
  onClose,
  onConfirm
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4">
      <div className="w-full max-w-xl rounded-[28px] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Clear All Vouchers</h3>
            <p className="mt-2 text-sm text-slate-500">This removes all voucher history but keeps the current balance preserved.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700">
            Close
          </button>
        </div>

        <div className="mt-5 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm text-slate-700">
          <p><span className="font-semibold text-slate-900">Total Voucher Count:</span> {clearAllPreview.voucherCount}</p>
          <p className="mt-2"><span className="font-semibold text-slate-900">Total Debit Amount:</span> {formatCurrency(clearAllPreview.expenseTotal)}</p>
          <p className="mt-2"><span className="font-semibold text-slate-900">Total Credit Amount:</span> {formatCurrency(clearAllPreview.allowanceTotal)}</p>
          <p className="mt-2"><span className="font-semibold text-slate-900">Preserved Balance:</span> {formatCurrency(clearAllPreview.preservedBalance)}</p>
        </div>

        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-slate-700">Type YES to confirm</span>
            <input
              value={clearConfirmationText}
              onChange={(event) => setClearConfirmationText(event.target.value)}
              placeholder="YES"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={clearConfirmationText !== "YES" || clearAllPreview.voucherCount === 0 || clearingVoucher}
            className="rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {clearingVoucher ? "Clearing..." : "Clear All"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className="mt-2 text-sm text-slate-700">{value || "-"}</p>
    </div>
  );
}

function SummaryPill({ label, value }) {
  return (
    <div className="rounded-[10px] bg-emerald-50 px-3 py-2 text-emerald-800 shadow-sm ring-1 ring-emerald-100 md:rounded-2xl md:px-4 md:py-3">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] md:text-xs md:tracking-[0.16em]">{label}</span>
      <p className="mt-1 text-sm font-semibold leading-5 md:font-bold">{value}</p>
    </div>
  );
}

export default function Vouchers() {
  const [useRedesigned] = useState(true);

  if (useRedesigned) {
    return (
      <EngineerLayout>
        <div className="space-y-4">
          <VoucherSimpleManager />
        </div>
      </EngineerLayout>
    );
  }

  return <LegacyVouchers />;
}

